import ptBR from "@/i18n/pt_BR/lang/survey.json";
import enUS from "@/i18n/en_US/lang/survey.json";
import esES from "@/i18n/es_ES/lang/survey.json";
import { QUESTION_TYPE } from "@/consts/survey";
import { JUMP_ACTIONS } from "@/consts/jumpLogic";
import { getCes2MaxButtonScale, getCes2MinButtonScale, getCes2Question } from "@/utils/getCes2Labels";

const commentDefault = {
  pt_BR: ptBR.default_questions.comment,
  en_US: enUS.default_questions.comment,
  es_ES: esES.default_questions.comment
};
const questionCes = {
  pt_BR: ptBR.default_questions.ces,
  en_US: enUS.default_questions.csat,
  es_ES: esES.default_questions.ces
};
const questionCsat = {
  pt_BR: ptBR.default_questions.csat,
  en_US: enUS.default_questions.csat,
  es_ES: esES.default_questions.csat
};
export const questionNpsTransactional = {
  pt_BR: ptBR.default_questions.nps_transactional,
  en_US: enUS.default_questions.nps_transactional,
  es_ES: esES.default_questions.nps_transactional
};
const question = {
  pt_BR: ptBR.default_questions.basic_label,
  en_US: enUS.default_questions.basic_label,
  es_ES: esES.default_questions.basic_label
};
const buttonSubtitleCsatMin = {
  pt_BR: ptBR.default_questions.csat_min,
  en_US: enUS.default_questions.csat_min,
  es_ES: esES.default_questions.csat_min
};
const buttonSubtitleCsatMax = {
  pt_BR: ptBR.default_questions.csat_max,
  en_US: enUS.default_questions.csat_max,
  es_ES: esES.default_questions.csat_max
};
const buttonSubtitleCesMin = {
  pt_BR: ptBR.default_questions.ces_min,
  en_US: enUS.default_questions.ces_min,
  es_ES: esES.default_questions.ces_min
};
const buttonSubtitleCesMax = {
  pt_BR: ptBR.default_questions.ces_max,
  en_US: enUS.default_questions.ces_max,
  es_ES: esES.default_questions.ces_max
};

const DEFAULT_COMPOSITE_QUESTION = {
  pt_BR: ptBR.default_questions.composite,
  en_US: enUS.default_questions.composite,
  es_ES: esES.default_questions.composite
};

export default class SurveyItem {
  createSurveyItem(type, organizationName) {
    let item = null;
    if (type === QUESTION_TYPE.LABEL) {
      item = {
        type: type,
        id: new Date().getTime(),
        question: {
          pt_BR: ptBR.default_questions.insert_label,
          en_US: enUS.default_questions.insert_label,
          es_ES: esES.default_questions.insert_label
        }
      };
    } else if (type === QUESTION_TYPE.IMAGE) {
      item = {
        type: type,
        id: new Date().getTime(),
        url: "",
        extension: "",
        align: "center"
      };
    } else if (type === QUESTION_TYPE.OPEN) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...question },
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        items: [],
        jump_rules_enabled: false,
        is_obligatory: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.RATINGS) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...question },
        comment_question: { ...commentDefault },
        min_scale: 1,
        max_scale: 5,
        button_design: 1,
        color_pattern: 1,
        custom_color: "#aaabb3",
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.CES2) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...getCes2Question() },
        comment_question: { ...commentDefault },
        scale_type: 0,
        min_scale: 1,
        max_scale: 5,
        button_design: 1,
        button_subtitle_min: { ...getCes2MinButtonScale(5) },
        button_subtitle_max: { ...getCes2MaxButtonScale(5) },
        color_pattern: 1,
        custom_color: "#aaabb3",
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.NPS_RELATIONAL || type === QUESTION_TYPE.NPS_TRANSACTIONAL) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question:
          type === QUESTION_TYPE.NPS_TRANSACTIONAL
            ? { ...questionNpsTransactional }
            : { ...questionNPS(organizationName) },
        detractor_comment_question: { ...commentDefault },
        neutral_comment_question: { ...commentDefault },
        promoter_comment_question: { ...commentDefault },
        button_design: 1,
        color_pattern: 1,
        inverted_scale: false,
        is_obligatory: false,
        is_obligatory_detractor: false,
        is_obligatory_neuter: false,
        is_obligatory_promotor: false,
        min_scale: 0,
        max_scale: 10,
        custom_color: "#aaabb3",
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.THUMBS) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...question },
        detractor_comment_question: { ...commentDefault },
        promoter_comment_question: { ...commentDefault },
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.CES || type === QUESTION_TYPE.CSAT) {
      let subtitleMin = {};
      let subtitleMax = {};
      let questionTitle = {};

      if (type === QUESTION_TYPE.CES) {
        subtitleMin = buttonSubtitleCesMin;
        subtitleMax = buttonSubtitleCesMax;
        questionTitle = { ...questionCes };
      } else {
        subtitleMin = buttonSubtitleCsatMin;
        subtitleMax = buttonSubtitleCsatMax;
        questionTitle = { ...questionCsat };
      }

      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: questionTitle,
        comment_question: { ...commentDefault },
        button_design: 1,
        color_pattern: 1,
        min_scale: 1,
        max_scale: 5,
        scale_type: 0,
        button_subtitle_min: { ...subtitleMin },
        button_subtitle_max: { ...subtitleMax },
        custom_color: "#aaabb3",
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else if (type === QUESTION_TYPE.EMOJI) {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...question },
        comment_question: { ...commentDefault },
        button_design: 3,
        color_pattern: 1,
        min_scale: 1,
        max_scale: 5,
        custom_color: "#aaabb3",
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    } else {
      item = {
        type: type,
        weight: 0,
        id: new Date().getTime(),
        question: { ...question },
        comment_question: { ...commentDefault },
        horizontal_align: false,
        multiple_answers: false,
        option_other: false,
        randomize: false,
        options: [],
        composite_question: { ...DEFAULT_COMPOSITE_QUESTION },
        comments_enabled: true,
        is_obligatory: false,
        items: [],
        jump_rules_enabled: false,
        jump_rules: {
          detractor: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          passive: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          },
          promoter: {
            action: JUMP_ACTIONS.NEXT_QUESTION,
            value: null
          }
        }
      };
    }

    return item;
  }
}

export const questionNPS = (organizationName) => {
  return {
    pt_BR: ptBR.default_questions.nps_with_org.replace("{organizationName}", organizationName),
    en_US: enUS.default_questions.nps_with_org.replace("{organizationName}", organizationName),
    es_ES: esES.default_questions.nps_with_org.replace("{organizationName}", organizationName)
  };
};
